import { format } from 'date-fns';

import { ReactComponent as FileHistoryIcon } from '@assets/icons/file-history.svg';
import { capitalizeFirstLetter } from '@utils/stringFormat';

import {
  ACTION_TYPE_MAP,
  ActivityTypeOptions,
  createMessageConfigs,
  ENTITY_CONFIGS,
  ENTITY_FORMAT_MAP,
  ENTITY_ICON_MAP,
  ENTITY_TITLE_OVERRIDE,
  LEAD_FIELD_CONFIGS,
  TaskStatus,
  TaskStatusEnum
} from '../constants/leadActivity';
import {
  DeleteModalProps,
  FieldConfig,
  ModifiedDataItem,
  ModifiedDataKey,
  TimelineAction,
  TimelineEntity,
  TimelineItemAPI
} from '../types/leadActivity';

export const getEntityIcon = (entity: TimelineEntity) =>
  ENTITY_ICON_MAP[entity] || FileHistoryIcon;

const formatAmount = (amount: string, currencySymbol?: string): string => {
  const numericAmount = Number(amount);
  if (Number.isNaN(numericAmount)) return amount;

  const formatted = numericAmount.toLocaleString('en-IN');
  return currencySymbol ? `${currencySymbol} ${formatted}` : formatted;
};

export const generateTitle = (item: TimelineItemAPI) => {
  const { entity, action_type: actionType, updated_by: updatedBy } = item;
  const args = item.arguments;

  if (
    entity === TimelineEntity.PAYMENT_ORDER &&
    actionType === TimelineAction.CLOSE
  ) {
    const amount = args?.closed_amount || args?.total_amount;
    const formattedAmount = amount
      ? ` of ${formatAmount(amount, args?.currency_symbol)}`
      : '';
    return `Payment${formattedAmount} closed by ${updatedBy}`;
  }

  if (
    entity === TimelineEntity.PAYMENT_ORDER &&
    actionType === TimelineAction.PAID
  ) {
    const formattedAmount = args?.amount
      ? formatAmount(args.amount, args.currency_symbol)
      : '';
    return formattedAmount ? `${formattedAmount} paid online` : 'Paid online';
  }

  const titleOverride = ENTITY_TITLE_OVERRIDE[entity]?.[actionType];
  if (titleOverride) {
    if (actionType === TimelineAction.SYNC) {
      return `${titleOverride} from ${updatedBy}`;
    }
    return `${titleOverride} by ${updatedBy}`;
  }

  const formatEntity = (entityStr: string) =>
    ENTITY_FORMAT_MAP[entityStr as TimelineEntity] ||
    entityStr
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  return `${formatEntity(entity)} ${
    ACTION_TYPE_MAP[actionType] || actionType?.toLowerCase() || ''
  } by ${updatedBy}`;
};

export const generateFieldUpdates = (
  modifiedData: ModifiedDataItem[],
  fieldConfigs: readonly FieldConfig[],
  fieldFormatter?: (field: string) => string
): string => {
  const updatedFields: string[] = [];

  const isDateField = (key: string): boolean =>
    [
      ModifiedDataKey.DATE,
      ModifiedDataKey.START_DATE,
      ModifiedDataKey.END_DATE
    ].includes(key as ModifiedDataKey);

  const formatDateValue = (value: string): string => {
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return format(date, 'dd MMM yyyy');
    } catch {
      return value;
    }
  };

  fieldConfigs.forEach(({ key, displayName }) => {
    const data = modifiedData.find((d) => d.key === key);

    if (!data) return;

    if (
      key === ModifiedDataKey.ASSIGNEE &&
      data.previous_value === '' &&
      data.current_value === ''
    ) {
      updatedFields.push(`${displayName} cleared`);
      return;
    }

    if (data.previous_value !== data.current_value) {
      const fieldName = fieldFormatter
        ? fieldFormatter(displayName)
        : displayName;

      if (data.previous_value && data.current_value) {
        const currentValue = isDateField(key)
          ? formatDateValue(data.current_value)
          : data.current_value;
        updatedFields.push(`${fieldName} updated to "${currentValue}"`);
      } else if (data.current_value) {
        const currentValue = isDateField(key)
          ? formatDateValue(data.current_value)
          : data.current_value;
        updatedFields.push(`${fieldName} set to "${currentValue}"`);
      } else if (data.previous_value) {
        updatedFields.push(`${fieldName} cleared`);
      }
    }
  });

  return updatedFields.join('\n');
};

export const createDataMap = (
  modifiedData: ModifiedDataItem[]
): Map<string, ModifiedDataItem> => {
  const map = new Map<string, ModifiedDataItem>();
  modifiedData.forEach((item) => {
    map.set(item.key, item);
  });
  return map;
};

export const getCreateMessage = (
  entity: TimelineEntity,
  dataMap: Map<string, ModifiedDataItem>
): string => {
  const config = createMessageConfigs.find(
    (messageConfig) => messageConfig.entity === entity
  );
  if (!config) return '';

  const data = dataMap.get(config.key);
  return data?.current_value
    ? config.template.replace(
        '{value}',
        data.current_value?.replace(/[\r\n]+$/g, '')
      )
    : config.fallback;
};

const ACTIVITY_REMARK_ENTITIES: readonly TimelineEntity[] = [
  TimelineEntity.LEAD_ACTIVITY_CALL,
  TimelineEntity.LEAD_ACTIVITY_TASK,
  TimelineEntity.LEAD_ACTIVITY_EVENT
];

export const appendCreateRemarks = (
  entity: TimelineEntity,
  dataMap: Map<string, ModifiedDataItem>,
  message: string
): string => {
  if (!ACTIVITY_REMARK_ENTITIES.includes(entity)) return message;

  const remarks = dataMap
    .get(ModifiedDataKey.REMARKS)
    ?.current_value?.replace(/[\r\n]+$/g, '')
    .trim();

  if (!remarks) return message;

  return message ? `${message}\nRemarks: ${remarks}` : `Remarks: ${remarks}`;
};

export const getEntityFieldConfigs = (
  entity: TimelineEntity
): readonly FieldConfig[] => {
  const entityFieldMaps: Record<TimelineEntity, () => readonly FieldConfig[]> =
    {
      [TimelineEntity.LEAD]: () => LEAD_FIELD_CONFIGS,
      [TimelineEntity.LEAD_ACTIVITY_CALL]: () => {
        const config = ENTITY_CONFIGS.call;
        return config.fields.map((field) => ({
          key: field,
          displayName: config.fieldFormatter(field)
        }));
      },
      [TimelineEntity.LEAD_ACTIVITY_TASK]: () => {
        const config = ENTITY_CONFIGS.task;
        return config.fields.map((field) => ({
          key: field,
          displayName: config.fieldFormatter(field)
        }));
      },
      [TimelineEntity.LEAD_ACTIVITY_EVENT]: () => {
        const config = ENTITY_CONFIGS.event;
        return config.fields.map((field) => ({
          key: field,
          displayName: config.fieldFormatter(field)
        }));
      },
      [TimelineEntity.LEAD_NOTES]: () => [],
      [TimelineEntity.LEAD_TAGS_MAP]: () => [],
      [TimelineEntity.LEAD_ATTACHMENTS]: () => [],
      [TimelineEntity.PAYMENT_ORDER]: () => [],
      [TimelineEntity.GROUP_MEMBER]: () => [],
      [TimelineEntity.BRANCH_MEMBER]: () => [],
      [TimelineEntity.LEAD_VOICE_NOTES]: () => []
    };

  return entityFieldMaps[entity]?.() || [];
};

export const handleNotesDescription = (
  dataMap: Map<string, ModifiedDataItem>
): string => {
  const noteData = dataMap.get(ModifiedDataKey.NOTE);
  const documentData = Array.from(dataMap.values()).find(
    (item) => item.document
  );

  const deletedDocumentData = Array.from(dataMap.values()).find(
    (item) =>
      item.key === 'document' && item.previous_value && !item.current_value
  );

  let noteText = '';
  if (noteData?.previous_value && noteData?.current_value) {
    noteText = `Note updated to "${noteData.current_value?.replace(
      /[\r\n]+$/g,
      ''
    )}"`;
  } else if (noteData?.current_value) {
    noteText = `"${noteData.current_value?.replace(/[\r\n]+$/g, '')}"`;
  }

  const documentText = documentData?.document?.[0]
    ? `Document: ${documentData.document[0].file_name}`
    : '';

  const deletedDocumentText = deletedDocumentData
    ? `Document "${deletedDocumentData.previous_value}" removed`
    : '';

  if (noteText && documentText) {
    return `${noteText}\n${documentText}`;
  }

  if (noteText && deletedDocumentText) {
    return `${noteText}\n${deletedDocumentText}`;
  }

  return noteText || documentText || deletedDocumentText;
};

export const handleTagsDescription = (
  dataMap: Map<string, ModifiedDataItem>
): string => {
  const tagData = dataMap.get(ModifiedDataKey.TAG);
  return tagData?.current_value ? `Tag: ${tagData.current_value}` : '';
};

export const handleLegacyDescription = (
  dataMap: Map<string, ModifiedDataItem>
): string => {
  const modifiedData = Array.from(dataMap.values());

  const legacyFieldConfigs: readonly FieldConfig[] = [
    { key: ModifiedDataKey.STATUS, displayName: 'Status' },
    { key: ModifiedDataKey.SOURCE, displayName: 'Source' },
    { key: ModifiedDataKey.MOBILE_NUMBER, displayName: 'Mobile number' }
  ] as const;

  return generateFieldUpdates(modifiedData, legacyFieldConfigs);
};

export const handlePaymentOrderDescription = (
  item: TimelineItemAPI
): string => {
  const { action_type: actionType } = item;
  const args = item.arguments;

  if (actionType === TimelineAction.MARK_PAID) {
    if (args?.amount && args?.payment_method) {
      const formattedAmount = formatAmount(args.amount, args.currency_symbol);
      return `${formattedAmount} marked as paid via ${args.payment_method}.`;
    }

    const paymentData = item.modified_data.find(
      (d) => d.key === ModifiedDataKey.PAYMENT_ORDER
    );
    if (paymentData?.current_value) {
      return paymentData.current_value;
    }
  }

  if (actionType === TimelineAction.SEND_REMINDER) {
    if (args?.total_amount) {
      const formattedAmount = formatAmount(
        args.total_amount,
        args.currency_symbol
      );
      return `Reminder sent for ${formattedAmount}.`;
    }
    return 'Reminder sent.';
  }

  if (actionType === TimelineAction.DISABLE) {
    if (args?.total_amount) {
      const formattedAmount = formatAmount(
        args.total_amount,
        args.currency_symbol
      );
      return `Payment link of ${formattedAmount} disabled.`;
    }
  }

  if (actionType === TimelineAction.CLOSE) {
    if (args?.note) {
      return `Note: ${args.note}`;
    }

    const paymentData = item.modified_data.find(
      (d) => d.key === ModifiedDataKey.PAYMENT_ORDER
    );
    if (paymentData?.current_value) {
      return paymentData.current_value;
    }
  }

  if (actionType === TimelineAction.ADD_DISCOUNT) {
    if (args?.discount_amount) {
      const formattedAmount = formatAmount(
        args.discount_amount,
        args.currency_symbol
      );
      return `Discounted amount: ${formattedAmount}`;
    }
  }

  if (actionType === TimelineAction.ENABLE_SPLIT_PAYMENT) {
    if (args?.total_amount) {
      return `Amount: ${formatAmount(args.total_amount, args.currency_symbol)}`;
    }
  }

  if (actionType === TimelineAction.CREATE) {
    const lines: string[] = [];
    if (args?.total_amount) {
      lines.push(
        `Amount: ${formatAmount(args.total_amount, args.currency_symbol)}`
      );
    }
    const placement: string[] = [];
    if (args?.branch_name) {
      placement.push(`Branch: ${args.branch_name}`);
    }
    if (args?.group_name) {
      placement.push(`Group: ${args.group_name}`);
    }
    if (placement.length) {
      lines.push(placement.join(', '));
    }
    if (lines.length) {
      return lines.join('\n');
    }
  }

  const paymentData = item.modified_data.find(
    (d) => d.key === ModifiedDataKey.PAYMENT_ORDER
  );
  if (paymentData?.current_value) {
    return paymentData.current_value;
  }

  return '';
};

export const generateDescription = (item: TimelineItemAPI): string => {
  const {
    modified_data: modifiedData,
    action_type: actionType,
    entity,
    entity_name: entityName
  } = item;

  if (entity === TimelineEntity.LEAD && actionType === TimelineAction.SYNC) {
    const sourceData = modifiedData.find(
      (d) => d.key === ModifiedDataKey.SOURCE
    );
    if (!sourceData?.current_value || !sourceData?.metadata) return '';
    const { metadata } = sourceData;
    const { ad_name: adName, business_name: businessName, provider } = metadata;
    const firstLine = `Source: Facebook Ad${adName ? `: ${adName}` : ''}`;
    const secondLine = businessName
      ? `${provider} (${businessName})`
      : provider;
    return `${firstLine}\n${secondLine}`;
  }

  if (entity === TimelineEntity.PAYMENT_ORDER) {
    return handlePaymentOrderDescription(item);
  }

  const dataMap = createDataMap(modifiedData);

  if (actionType === TimelineAction.CREATE) {
    return appendCreateRemarks(
      entity,
      dataMap,
      getCreateMessage(entity, dataMap)
    );
  }

  if (actionType === TimelineAction.DELETE) {
    return `${ENTITY_FORMAT_MAP[entity] ?? entity} ${
      entityName ?? ''
    } no longer exists`;
  }

  switch (entity) {
    case TimelineEntity.LEAD:
    case TimelineEntity.LEAD_ACTIVITY_CALL:
    case TimelineEntity.LEAD_ACTIVITY_TASK:
    case TimelineEntity.LEAD_ACTIVITY_EVENT: {
      const fieldConfigs = getEntityFieldConfigs(entity);
      return generateFieldUpdates(modifiedData, fieldConfigs);
    }

    case TimelineEntity.LEAD_NOTES:
      return handleNotesDescription(dataMap);

    case TimelineEntity.LEAD_TAGS_MAP:
      return handleTagsDescription(dataMap);

    case TimelineEntity.GROUP_MEMBER:
    case TimelineEntity.BRANCH_MEMBER: {
      const memberData = dataMap.get(entity);
      return memberData?.current_value || '';
    }

    default:
      return handleLegacyDescription(dataMap);
  }
};

export const getDeleteModalTitle = (item: { type: string } | null) => {
  if (!item) return 'Delete Item';
  if (item.type === 'event') return 'Delete Meeting';
  return `Delete ${capitalizeFirstLetter(item.type)}`;
};

export const getDeleteModalMessage = (item: DeleteModalProps | null) => {
  if (!item) return 'Are you sure you want to delete this item?';
  const itemName = item?.name ? ` "${item.name}"` : '';

  let typeLabel = '';
  if (item?.type === ActivityTypeOptions.CALL) {
    typeLabel = 'call info';
  } else if (item?.type === ActivityTypeOptions.EVENT) {
    typeLabel = 'meeting';
  } else {
    typeLabel = 'task';
  }

  return `Are you sure you want to delete this ${typeLabel} ${itemName}? This action cannot be undone.`;
};

export const splitTimestamp = (timestamp: string) => {
  if (!timestamp) return { date: '', time: '' };

  try {
    const date = new Date(timestamp);
    const dateStr = date.toISOString().split('T')[0];

    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const timeStr = `${displayHours}:${minutes
      .toString()
      .padStart(2, '0')} ${ampm}`;

    return { date: dateStr, time: timeStr };
  } catch (error) {
    return { date: '', time: '' };
  }
};

// handle completed status selection
export const isTaskOverdue = (date: string, time: string) => {
  // date format = '2025-09-19';
  // time format = '01:00 AM';
  let hours = 0;
  let minutes = 0;
  if (time) {
    const [timePart, ampm] = time.split(' ');
    if (timePart && ampm) {
      let h = Number(timePart.split(':')[0]);
      minutes = Number(timePart.split(':')[1]);
      if (ampm.toUpperCase() === 'PM' && h !== 12) {
        h += 12;
      }
      if (ampm.toUpperCase() === 'AM' && h === 12) {
        h = 0;
      }
      hours = h;
    }
  }
  const [year, month, day] = date.split('-').map(Number);

  const taskDate = new Date(year, month - 1, day, hours, minutes);
  const currentDate = new Date();
  currentDate.setSeconds(0, 0);
  return taskDate < currentDate;
};

export const getTaskStatusOptions = (date: string, time: string) => {
  if (date && time) {
    const isOverdue = isTaskOverdue(date, time);
    if (isOverdue) {
      return TaskStatus.filter(
        (status) => status.value !== TaskStatusEnum.SCHEDULED
      );
    }
  }

  return TaskStatus.filter((status) => status.value !== TaskStatusEnum.OVERDUE);
};

export const getTaskStatus = (date: string, time: string, status: string) => {
  if (status === TaskStatusEnum.COMPLETED) {
    return TaskStatusEnum.COMPLETED;
  }

  return isTaskOverdue(date, time)
    ? TaskStatusEnum.OVERDUE
    : TaskStatusEnum.SCHEDULED;
};
